import { createSlice } from "@reduxjs/toolkit";
import { WaitlistEntryResponse } from "./contracts/WaitlistEntryResponse";
import {
  addWaitlistEntry,
  convertWaitlistEntry,
  fetchSessionWaitlist,
  removeWaitlistEntry,
} from "./slice-extensions/waitlist-slice-extension";

interface WaitlistState {
  /** Entries per session id, oldest first (first come first served). */
  entriesBySession: Record<string, WaitlistEntryResponse[]>;
  status: "idle" | "loading" | "succeeded" | "failed";
}

const initialState: WaitlistState = {
  entriesBySession: {},
  status: "idle",
};

const waitlistSlice = createSlice({
  name: "waitlist",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionWaitlist.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchSessionWaitlist.fulfilled, (state, action) => {
        state.entriesBySession[action.payload.sessionId] = action.payload.entries;
        state.status = "succeeded";
      })
      .addCase(fetchSessionWaitlist.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(addWaitlistEntry.fulfilled, (state, action) => {
        const entries = state.entriesBySession[action.payload.bookingId] ?? [];
        state.entriesBySession[action.payload.bookingId] = [...entries, action.payload];
      })
      .addCase(removeWaitlistEntry.fulfilled, (state, action) => {
        const { sessionId, entryId } = action.payload;
        state.entriesBySession[sessionId] =
          (state.entriesBySession[sessionId] ?? []).filter((e) => e.id !== entryId);
      })
      .addCase(convertWaitlistEntry.fulfilled, (state, action) => {
        const { sessionId, entryId } = action.payload;
        state.entriesBySession[sessionId] =
          (state.entriesBySession[sessionId] ?? []).filter((e) => e.id !== entryId);
      });
  },
});

export const selectSessionWaitlist = (sessionId: string) =>
  (state: { waitlist: WaitlistState }) => state.waitlist.entriesBySession[sessionId] ?? [];

export const selectWaitlistStatus = (state: { waitlist: WaitlistState }) =>
  state.waitlist.status;

export default waitlistSlice.reducer;
